import type {
  HTTPTransaction,
  TableCraftFunction,
  TableCraftObject,
  TableCraftRow,
  Transaction,
} from '@carboncopyco/types';
import {
  removeUndefinedValues,
  transformToCamelCase,
} from '@carboncopyco/utils';
import { createBaseApi } from '../utils/createBaseApi';

export interface SearchTransactionsArgs {
  organizationId: string;
  entityId: string;
  htmlElementString: string;
  referer?: string;
}

export interface SearchTransactionsResponse {
  total: number;
  results: Transaction[];
  searchString: string;
}

interface TableCraftObjectsArgs {
  organizationId: string;
  draftId: string;
  searchQuery?: string;
  size?: number;
  page?: number;
}

export type TableCraftObjectsResponse =
  | TableCraftObject[]
  | {
      status: 'indexing';
    };

interface TableCraftRowsArgs {
  organizationId: string;
  draftId: string;
  objectId: string;
  searchQuery?: string;
  size?: number;
  page?: number;
}

interface UpdateTableCraftRowArgs {
  organizationId: string;
  draftId: string;
  objectId: string;
  row_id: string;
  key: string;
  value: string | boolean;
  id_column: string;
  enrich: boolean;
}

interface UpdateTableCraftColumnArgs {
  organizationId: string;
  draftId: string;
  objectId: string;
  col_name: string;
  generator: string;
  generator_args?: Record<string, unknown> | string;
  enrich: boolean;
}

interface UpdateTableCraftSettingsArgs {
  organizationId: string;
  draftId: string;
  objectId: string;
  hide: boolean;
  display_name: string;
  filter_columns: string[];
  set_object_pk: string;
}

interface RevertTableCraftModificationArgs {
  organizationId: string;
  draftId: string;
  entity: 'cell' | 'column' | 'row';
  objectId: string;
  row_id?: string;
  col_name?: string;
  enrich: boolean;
}

interface VisualTableCraftSearchArgs {
  organizationId: string;
  draftId: string;
  htmlElementString: string;
  referer?: string;
}

interface TableCraftFunctionResponse {
  functions: TableCraftFunction[];
}

interface TableCraftEnrichmentColumnsArgs {
  organizationId: string;
  draftId: string;
  objectId: string;
  col_name: string;
}

export const [tableCraftApi, initTableCraftApi] = createBaseApi(
  {
    name: 'tableCraftApi',
    endpoints: (builder) => ({
      searchTransactions: builder.query<
        SearchTransactionsResponse,
        SearchTransactionsArgs
      >({
        query: ({ organizationId, entityId, htmlElementString, referer }) => ({
          responseHandler: async (response) => {
            const json: SearchTransactionsResponse = await response.json();
            const camelCasedResponse = transformToCamelCase(json, []);

            camelCasedResponse.results.forEach((transaction, i) => {
              const revertHeadersCasing = (
                camelCasedTransaction: HTTPTransaction,
                originalCasedTransaction: HTTPTransaction
              ) =>
                ['request', 'response'].forEach((transactionType) => {
                  camelCasedTransaction[transactionType].headers =
                    originalCasedTransaction[transactionType].headers;
                });

              revertHeadersCasing(
                transaction.original,
                json.results[i].original
              );
              if (transaction.modified) {
                revertHeadersCasing(
                  transaction.modified,
                  json.results[i].modified
                );
              }
            });

            return camelCasedResponse;
          },
          url: `/table_craft/v1/${organizationId}/draft/${entityId}/search`,
          method: 'POST',
          body: {
            html_element_string: htmlElementString,
            referer,
          },
        }),
      }),
      updateTableCraftRow: builder.mutation<null, UpdateTableCraftRowArgs>({
        query: ({
          organizationId,
          draftId,
          objectId,
          row_id,
          key,
          value,
          id_column,
          enrich,
        }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/modify/row/${objectId}`,
          method: 'POST',
          body: {
            row_id,
            key,
            value,
            id_column,
            enrich,
          },
        }),
      }),
      updateTableCraftColumn: builder.mutation<
        null,
        UpdateTableCraftColumnArgs
      >({
        query: ({
          organizationId,
          draftId,
          objectId,
          col_name,
          generator,
          generator_args,
          enrich,
        }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/modify/column/${objectId}`,
          method: 'POST',
          body: {
            col_name,
            generator,
            generator_args,
            enrich,
          },
        }),
      }),
      getTableCraftObjects: builder.query<
        TableCraftObjectsResponse,
        TableCraftObjectsArgs
      >({
        query: ({ organizationId, draftId, searchQuery, size, page }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/objects`,
          method: 'GET',
          params: removeUndefinedValues({
            page,
            size,
            search_string: searchQuery,
            exact_match: false,
          }),
        }),
      }),

      getTableCraftRows: builder.query<TableCraftRow[], TableCraftRowsArgs>({
        query: ({
          organizationId,
          draftId,
          objectId,
          searchQuery,
          size,
          page,
        }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/rows/${objectId}`,
          method: 'GET',
          params: removeUndefinedValues({
            page,
            size,
            search_string: searchQuery,
            exact_match: false,
          }),
        }),
      }),

      updateTableCraftSettings: builder.mutation<
        null,
        UpdateTableCraftSettingsArgs
      >({
        query: ({
          organizationId,
          draftId,
          objectId,
          hide,
          display_name,
          filter_columns,
          set_object_pk,
        }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/settings/update`,
          method: 'POST',
          body: {
            object_id: objectId,
            hide,
            display_name,
            filter_columns,
            set_object_pk,
          },
        }),
      }),
      revertTableCraftModification: builder.mutation<
        null,
        RevertTableCraftModificationArgs
      >({
        query: ({
          organizationId,
          draftId,
          objectId,
          entity,
          row_id,
          col_name,
          enrich,
        }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/revert/${entity}/${objectId}`,
          method: 'POST',
          body: { row_id, col_name, enrich },
        }),
      }),
      visualTableCraftSearch: builder.mutation<
        TableCraftObject[],
        VisualTableCraftSearchArgs
      >({
        query: ({ organizationId, draftId, htmlElementString, referer }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/objects`,
          method: 'POST',
          body: {
            html_element_string: htmlElementString,
            referer,
          },
        }),
      }),
      getTableCraftFunctions: builder.query<TableCraftFunctionResponse, void>({
        query: () => ({
          url: '/table_craft/v1/functions',
          method: 'GET',
        }),
      }),

      getTableCraftColumnEnrichment: builder.query<
        string[],
        TableCraftEnrichmentColumnsArgs
      >({
        query: ({ organizationId, draftId, objectId, col_name }) => ({
          url: `/table_craft/v1/${organizationId}/draft/${draftId}/enrich/column/${objectId}`,
          method: 'GET',
          params: { col_name },
        }),
      }),
    }),
  },
  {
    transformCasing: false,
  }
);

export const {
  useSearchTransactionsQuery,
  useLazySearchTransactionsQuery,
  useGetTableCraftObjectsQuery,
  useLazyGetTableCraftObjectsQuery,
  useGetTableCraftRowsQuery,
  useLazyGetTableCraftRowsQuery,
  useUpdateTableCraftRowMutation,
  useUpdateTableCraftColumnMutation,
  useUpdateTableCraftSettingsMutation,
  useRevertTableCraftModificationMutation,
  useVisualTableCraftSearchMutation,
  useLazyGetTableCraftFunctionsQuery,
  useLazyGetTableCraftColumnEnrichmentQuery,
} = tableCraftApi;
